<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import SalesRegistrationPermissionState from '../components/SalesRegistrationPermissionState.vue';
import SalesDeliveryStartContent from '../components/SalesDeliveryStartContent.vue';
import { useSalesDeliveryStart } from '../composables/useSalesDeliveryStart';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const id = computed(() => route.params.salesTransactionId);
const start = useSalesDeliveryStart({ id, t, notify: useAlert });
const transactionTitle = computed(() => start.transaction.value?.title || '');
const transactionAddress = computed(
  () => start.transaction.value?.address?.endereco || ''
);
const headerSubtitle = computed(() =>
  [transactionTitle.value, transactionAddress.value].filter(Boolean).join(' · ')
);

const registrationLocation = () => ({
  name: 'operacao_imobiliaria_vendas_cadastro',
  params: {
    accountId: route.params.accountId,
    salesTransactionId: id.value,
  },
});

const goBack = () => router.push(registrationLocation());
const goToEvaluation = () => {
  if (!start.canContinue.value) return;
  router.push({
    name: 'operacao_imobiliaria_vendas_entregar_avaliando',
    params: {
      accountId: route.params.accountId,
      salesTransactionId: id.value,
    },
  });
};
</script>

<template>
  <div class="min-h-full bg-lais-bg-soft">
    <SalesRegistrationPermissionState
      v-if="start.isForbidden.value"
      @back="goBack"
    />
    <template v-else>
      <LaisHeader
        :title="t('SALES.DELIVERY.START.HEADER_TITLE')"
        :subtitle="headerSubtitle || t('SALES.DELIVERY.START.HEADER_SUBTITLE')"
        :breadcrumb="[
          { label: t('SALES.TITLE') },
          { label: t('SALES.DELIVERY.TITLE') },
        ]"
        show-back
        :back-label="t('SALES.ACTIONS.BACK')"
        @back="goBack"
      />
      <div
        v-if="start.loading.value"
        class="mx-auto max-w-[1280px] px-4 py-12 text-center text-sm text-lais-text-muted sm:px-6 lg:px-10"
      >
        {{ t('SALES.DELIVERY.START.LOADING') }}
      </div>
      <div
        v-else-if="start.loadError.value"
        class="mx-auto max-w-[1280px] px-4 py-12 text-center text-sm text-lais-text-muted sm:px-6 lg:px-10"
      >
        {{ t('SALES.DELIVERY.START.ERRORS.LOAD') }}
      </div>
      <SalesDeliveryStartContent
        v-else
        :can-continue="start.canContinue.value"
        :can-edit="start.canEdit.value"
        :receipt-document="start.receipt.document.value"
        :receipt-type="start.receiptType.value"
        :removing="start.receipt.removing.value"
        :saving-type="start.savingType.value"
        :uploading="start.receipt.uploading.value"
        @back="goBack"
        @remove="start.removeReceipt"
        @select-type="start.selectReceiptType"
        @submit="goToEvaluation"
        @upload="start.uploadReceipt"
      />
    </template>
  </div>
</template>
